import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import { Reveal } from "./Reveal";

const photos = [
    { src: "/assets/work-2.webp", label: "Exterior Gloss" },
    { src: "/assets/work-1.webp", label: "Interior Ambient" },
    { src: "/assets/work-3.webp", label: "Cargo Reset" },
];

export const Gallery = () => {
    const [active, setActive] = useState(null);

    const onKey = useCallback((e) => {
        if (e.key === "Escape") setActive(null);
    }, []);

    useEffect(() => {
        if (active !== null) {
            window.addEventListener("keydown", onKey);
            document.body.style.overflow = "hidden";
        }
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [active, onKey]);

    return (
        <section id="gallery" data-testid="gallery-section" className="relative py-20 lg:py-32 bg-[#0a0a0e] border-y border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
                    <div>
                        <Reveal>
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">
                                The Work
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
                                Fresh off the <span className="text-zinc-500">driveway.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="text-sm text-zinc-500 max-w-xs leading-relaxed">
                            Real cars, real driveways, real results. Tap any frame to view it full-screen.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 auto-rows-[320px] sm:auto-rows-[380px] lg:auto-rows-[440px] gap-4 sm:gap-5">
                    {photos.map((p, i) => (
                        <Reveal key={p.label} delay={0.06 * i} y={24}>
                            <motion.button
                                data-testid={`gallery-image-${i}`}
                                onClick={() => setActive(i)}
                                whileHover={{ scale: 0.98 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                className="group relative w-full h-full rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-colors duration-500 text-left"
                            >
                                <img
                                    src={p.src}
                                    alt={`${p.label} — Pronto Auto Care`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-500" />
                                <div className="absolute inset-x-0 bottom-0 p-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <span className="font-code text-[10px] uppercase tracking-[0.25em] text-white">
                                        {p.label}
                                    </span>
                                    <Expand className="w-4 h-4 text-white" />
                                </div>
                            </motion.button>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active !== null && (
                    <motion.div
                        data-testid="lightbox-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setActive(null)}
                        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10"
                    >
                        <button
                            data-testid="lightbox-close-button"
                            onClick={() => setActive(null)}
                            aria-label="Close"
                            className="absolute top-5 right-5 p-3 rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <motion.img
                            key={active}
                            src={photos[active].src}
                            alt={photos[active].label}
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[85vh] max-w-full rounded-2xl border border-white/15 object-contain"
                        />
                        <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-code text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                            {photos[active].label}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
